import { useCallback, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { loyaltyLaneClient } from '@/services/api/loyalty-lane-client';
import { siteInfoQueryOptions, useSiteInfo } from '@/SiteInfo/hooks/use-site-info';
import type { SiteInfo } from '@/SiteInfo/types/site';
import { TokenStorage } from '../lib/token-storage';
import type { LLApiResponse, Shopper } from '../types/loyalty-lane';

/**
 * Nonce-based password reset for Loyalty Lane native auth.
 *
 * 1. requestReset   – POST {broker}/password-reset { contactInfo, resetUrl, … }
 *                     The broker asks LL for the nonce and emails the shopper
 *                     a link to `resetUrl?nonce=…`, as LL's integration guide
 *                     recommends. LL's own GET /PasswordResetToken never
 *                     contacts the user — it only hands the nonce back to the
 *                     caller — so delivery has to happen server-side, and the
 *                     broker only links to allowlisted origins.
 * 2. verifyNonce    – POST /PasswordResetToken { nonce } → reset-scoped ShopperToken
 *                     Called by the /forgot-password page when it is opened
 *                     from that link.
 * 3. setNewPassword – PUT /Shopper { shopperid, password } with that token
 *
 * The reset-scoped token is held in this hook only. It is never written to
 * TokenStorage, so a half-finished reset can't leave the site "signed in" or
 * seed the silent re-auth with a shopper id the user never authenticated as.
 *
 * The store name and support address the broker puts in the email come from
 * the Strapi site record (SiteTitle / SiteEmailAddress), with VITE_STORE_NAME
 * and VITE_CONTACT_EMAIL as optional overrides for parity with the mobile apps.
 */

export type PasswordResetErrorKind =
  | 'network'
  | 'invalid_contact'
  | 'invalid_code'
  | 'server'
  | 'unknown';

export class PasswordResetError extends Error {
  kind: PasswordResetErrorKind;
  constructor(message: string, kind: PasswordResetErrorKind) {
    super(message);
    this.name = 'PasswordResetError';
    this.kind = kind;
  }
}

interface ResetSession {
  token: string;
  shopperid: number;
  accountid: number;
}

const MSG_NETWORK = 'Unable to connect. Please check your connection and try again.';
const MSG_SERVER = 'Unable to start a password reset right now. Please try again.';
const MSG_INVALID_CODE = 'This reset link is invalid or has expired. Please request a new one.';

/** Page the emailed link opens. The broker appends `?nonce=`. */
export const RESET_PATH = '/forgot-password';
const MSG_SESSION_LOST = 'Your reset session has expired. Please start over.';
const MSG_UPDATE_FAILED = 'Failed to update password. Please try again.';

/** Extract a human-readable message from a JSON error body like {"error":"..."} */
function parseErrorMessage(body: string): string | null {
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === 'string') return parsed.error;
    if (typeof parsed?.message === 'string') return parsed.message;
  } catch {
    if (body && !body.startsWith('{') && !body.startsWith('<')) return body;
  }
  return null;
}

/**
 * LL's casing is inconsistent across endpoints (ShopperSession returns
 * `token`/`shopperid`; the nonce exchange is documented loosely). Accept
 * either so a casing mismatch can't make every valid code look "invalid".
 */
function normalizeResetSession(data: unknown): ResetSession | null {
  if (!data || typeof data !== 'object') return null;
  const obj = data as Record<string, unknown>;
  const pick = (...keys: string[]) => {
    for (const k of keys) if (obj[k] !== undefined && obj[k] !== null) return obj[k];
    return undefined;
  };
  const isValid = pick('IsValid', 'isvalid', 'isValid');
  if (isValid === false) return null;

  const token = pick('ShopperToken', 'shoppertoken', 'token', 'Token');
  const shopperid = Number(pick('ShopperId', 'ShopperID', 'shopperid'));
  const accountid = Number(pick('AccountId', 'AccountID', 'accountid'));
  if (typeof token !== 'string' || !token.trim()) return null;
  if (!Number.isFinite(shopperid)) return null;

  return {
    token: token.trim(),
    shopperid,
    accountid: Number.isFinite(accountid) ? accountid : 0,
  };
}

/** The shared client only refreshes the ClientToken on a 401 when it attached
 *  the token itself. These calls pass it explicitly (skipAuth), so do the same
 *  one-shot refresh here; otherwise a token LL has dropped makes every valid
 *  reset code look "invalid" until the local 20-hour expiry. */
function isClientTokenRejected(err: unknown): boolean {
  return err instanceof Error && /API error 401\b/.test(err.message);
}

async function withClientTokenRetry<T>(call: (clientToken: string) => Promise<T>): Promise<T> {
  try {
    return await call(await TokenStorage.getClientToken());
  } catch (err) {
    if (!isClientTokenRejected(err)) throw err;
    TokenStorage.clearClientToken();
    return call(await TokenStorage.getClientToken());
  }
}

export function usePasswordReset() {
  // Lives for the duration of the page; never persisted.
  const resetRef = useRef<ResetSession | null>(null);

  const queryClient = useQueryClient();
  const { isFetched: isSiteInfoReady } = useSiteInfo();

  /** Step 1: ask the broker to generate a nonce and email the shopper a reset link. */
  const requestReset = useMutation({
    mutationFn: async (contactInfo: string) => {
      const base = (import.meta.env.VITE_LL_SESSION_API || '').replace(/\/$/, '');
      if (!base) {
        throw new PasswordResetError('Password reset is not available on this site.', 'server');
      }

      // Wait for the Strapi site record rather than reading whatever has
      // rendered so far: a fast submit must not brand the email "Your Store".
      // If Strapi itself is down, fall back so the reset still goes out.
      let siteInfo: SiteInfo | null = null;
      try {
        siteInfo = await queryClient.ensureQueryData(siteInfoQueryOptions);
      } catch {
        siteInfo = null;
      }

      const storeName =
        import.meta.env.VITE_STORE_NAME || siteInfo?.SiteTitle || 'Your Store';
      const supportEmail =
        import.meta.env.VITE_CONTACT_EMAIL || siteInfo?.SiteEmailAddress || undefined;

      const payload = {
        contactInfo: contactInfo.trim(),
        storeName,
        supportEmail,
        // Where the emailed link lands. The broker rejects origins it hasn't
        // allowlisted, so a new domain needs adding there before it goes live.
        resetUrl: `${window.location.origin}${RESET_PATH}`,
        // Tenant key. The remaining credentials are the legacy path the broker
        // still accepts for tenants not yet in Parameter Store.
        clientUsername: import.meta.env.VITE_LOYALTY_LANE_CLIENT_USERNAME || '',
        clientPassword: import.meta.env.VITE_LOYALTY_LANE_CLIENT_PASSWORD || undefined,
        adminUsername: import.meta.env.VITE_LOYALTY_LANE_ADMIN_USERNAME || undefined,
        adminPassword: import.meta.env.VITE_LOYALTY_LANE_ADMIN_PASSWORD || undefined,
      };

      let res: Response;
      try {
        res = await fetch(`${base}/password-reset`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } catch {
        throw new PasswordResetError(MSG_NETWORK, 'network');
      }

      if (res.status >= 400 && res.status < 500) {
        const body = await res.text().catch(() => '');
        throw new PasswordResetError(
          parseErrorMessage(body) || 'Please check the email address and try again.',
          'invalid_contact',
        );
      }

      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new PasswordResetError(parseErrorMessage(body) || MSG_SERVER, 'server');
      }
    },
  });

  /** Step 2: exchange the nonce from the emailed link for a reset-scoped ShopperToken. */
  const verifyNonce = useMutation({
    mutationFn: async (nonce: string) => {
      let response;
      try {
        // skipAuth: send only our own ClientToken. The shared client would
        // otherwise attach any stored ShopperToken and, on a 401, try to
        // silently re-auth — neither belongs in a password reset.
        response = await withClientTokenRetry((clientToken) =>
          loyaltyLaneClient.post<LLApiResponse<unknown>>(
            'PasswordResetToken',
            { nonce: nonce.trim() },
            { skipAuth: true, headers: { ClientToken: clientToken } },
          ),
        );
      } catch (err) {
        if (err instanceof TypeError) throw new PasswordResetError(MSG_NETWORK, 'network');
        // LL reports a bad/expired nonce as an HTTP error.
        throw new PasswordResetError(MSG_INVALID_CODE, 'invalid_code');
      }

      if (response.data.result !== 'OK') {
        throw new PasswordResetError(MSG_INVALID_CODE, 'invalid_code');
      }

      const session = normalizeResetSession(response.data.data);
      if (!session) {
        throw new PasswordResetError(MSG_INVALID_CODE, 'invalid_code');
      }

      resetRef.current = session;
      return session;
    },
  });

  /** Step 3: set the new password with the reset-scoped token. */
  const setNewPassword = useMutation({
    mutationFn: async (password: string) => {
      const session = resetRef.current;
      if (!session) {
        throw new PasswordResetError(MSG_SESSION_LOST, 'invalid_code');
      }

      let response;
      try {
        response = await withClientTokenRetry((clientToken) =>
          loyaltyLaneClient.put<LLApiResponse<Shopper>>(
            'Shopper',
            { shopperid: session.shopperid, password },
            {
              skipAuth: true,
              headers: { ClientToken: clientToken, ShopperToken: session.token },
            },
          ),
        );
      } catch (err) {
        if (err instanceof TypeError) throw new PasswordResetError(MSG_NETWORK, 'network');
        throw new PasswordResetError(MSG_UPDATE_FAILED, 'server');
      }

      if (response.data.result !== 'OK') {
        throw new PasswordResetError(response.data.message || MSG_UPDATE_FAILED, 'server');
      }

      // The reset token is single-purpose; drop it. The user signs in normally.
      resetRef.current = null;
      return response.data.data;
    },
  });

  /** Discard any in-progress reset session (e.g. user backs out or closes the modal). */
  const cancel = useCallback(() => {
    resetRef.current = null;
  }, []);

  return { requestReset, verifyNonce, setNewPassword, cancel, isSiteInfoReady };
}
